/**
 * dom.ts — the two selector helpers, ported from oracle lines 640–641 (P1 US-112).
 *
 * The oracle's `$` and `$$`. Kept as thin wrappers with the same names rather than replaced by
 * direct `querySelector` calls throughout, so that a reviewer diffing a ported function against
 * the oracle's original sees the same shape of code and can check the logic rather than the
 * translation.
 *
 * Typed, which the originals were not, and that is the only change: a wrong element type is now a
 * compile error instead of a `null` dereference at the one moment a user clicks something.
 */

/** First match, or `null`. Oracle line 640. */
export function $<T extends Element = HTMLElement>(selector: string, root?: ParentNode): T | null {
  return (root ?? document).querySelector<T>(selector);
}

/**
 * First match, throwing if absent.
 *
 * Not in the oracle, which let a missing element become `null` and then a `TypeError` several
 * frames later. Every caller of this one is asking for something `index.html` statically contains
 * — `#form_lamine`, `#print`, `#projet` — so absence means the shell was edited, and failing at
 * the point of the lookup names the element instead of the symptom.
 */
export function must<T extends Element = HTMLElement>(selector: string, root?: ParentNode): T {
  const found = $<T>(selector, root);
  if (found === null) throw new Error(`Missing required element: ${selector}`);
  return found;
}

/** All matches, as an array. Oracle line 641. */
export function $$<T extends Element = HTMLElement>(selector: string, root?: ParentNode): T[] {
  return Array.from((root ?? document).querySelectorAll<T>(selector));
}

/**
 * Escape a string for interpolation into HTML.
 *
 * The oracle does not do this — it builds markup by concatenation and every value it interpolates
 * is its own constant, so nothing user-supplied ever reaches the markup. That is true of the field
 * definitions too. It stops being true at exactly one place: `repere`, the free-text element label
 * the user types, which US-116 prints into the note de calcul. Having the helper here means the
 * note renderer has something to reach for rather than inventing one under time pressure.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
